import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, ShoppingBag, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, CONTACT } from "../config";
import { meta } from "../lib/metaPixel";

const inputCls =
  "w-full rounded-xl border border-ink/15 bg-white px-5 py-4 text-sm text-ink placeholder:text-ink/35 outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-colors duration-300";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    const leadId = "lead_" + (crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36));
    try {
      await axios.post(`${API}/enquiries`, { ...form, lead_id: leadId });
      meta.lead({}, leadId);
      toast.success("Thank you! Your enquiry has been sent — we'll reach out shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-cream py-28 md:py-36 border-t border-ink/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-16 md:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-xs tracking-[0.4em] uppercase text-amberbrand mb-4">04 — Contact</p>
          <h2 className="font-serif text-5xl md:text-6xl text-ink tracking-tighter mb-6">
            Have questions? <span className="italic text-olive">Let's talk.</span>
          </h2>
          <p className="text-sm md:text-base font-light text-ink/60 leading-relaxed mb-10 max-w-md">
            Whether it's a bulk order, a retail partnership, or just a question
            about how our oils are made — we'd love to hear from you.
          </p>

          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <span className="mt-1 flex h-10 w-10 items-center justify-center rounded-full border border-gold/40 text-amberbrand">
                <Mail size={16} />
              </span>
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-ink/40 mb-1">Email</p>
                <a
                  data-testid="contact-email-link"
                  href={`mailto:${CONTACT.email}`}
                  className="text-base text-ink hover:text-amberbrand transition-colors duration-300"
                >
                  {CONTACT.email}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="mt-1 flex h-10 w-10 items-center justify-center rounded-full border border-gold/40 text-amberbrand">
                <MapPin size={16} />
              </span>
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-ink/40 mb-1">
                  {CONTACT.company}
                </p>
                <p data-testid="contact-address" className="text-base text-ink">{CONTACT.address}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="mt-1 flex h-10 w-10 items-center justify-center rounded-full border border-gold/40 text-amberbrand">
                <ShoppingBag size={16} />
              </span>
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-ink/40 mb-1">
                  Also available on
                </p>
                <p data-testid="contact-marketplaces" className="text-base text-ink">
                  Amazon · Instamart · Blinkit
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="rounded-3xl border border-ink/10 bg-white/60 backdrop-blur-sm p-8 md:p-10 space-y-5 self-start"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <input
              data-testid="contact-name-input"
              required
              value={form.name}
              onChange={set("name")}
              placeholder="Your name *"
              className={inputCls}
            />
            <input
              data-testid="contact-email-input"
              required
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="Email address *"
              className={inputCls}
            />
          </div>
          <input
            data-testid="contact-phone-input"
            value={form.phone}
            onChange={set("phone")}
            placeholder="Phone (optional)"
            className={inputCls}
          />
          <textarea
            data-testid="contact-message-input"
            required
            rows={5}
            value={form.message}
            onChange={set("message")}
            placeholder="Your message *"
            className={`${inputCls} resize-none`}
          />
          <motion.button
            data-testid="contact-submit-btn"
            type="submit"
            disabled={sending}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-ink px-8 py-4 text-sm font-medium tracking-wide text-cream hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-60"
          >
            <Send size={16} />
            {sending ? "Sending..." : "Send Enquiry"}
          </motion.button>
        </motion.form>
      </div>
    </section>
  );
};
